import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { keycloak, getUserInfo } from './auth/keycloak';
import { initKeycloakWithFallback } from './auth/keycloak-fallback';
import LoadingFallback from './components/LoadingFallback';
import versionService from './services/versionService';
import { initMediaHosts } from './lib/mediaHosts';
import { disableConsoleInProduction, logger } from './utils/logger';
import { AppProviders, portalContainerRef } from './app/AppProviders';
import { ClassroomRouteGuard, RedirectIfAuthed, RequireAuth } from './routes/RouteGuards';
import {
  adminStudentClassroomRoutes,
  adminStudentRoutes,
  adminSystemRoutes,
  adminUserRoutes,
} from './routes/AdminRoutes';
import {
  teacherAssignmentRoutes,
  teacherClassroomRoutes,
  teacherContentRoutes,
  teacherDashboardRoutes,
  teacherExerciseLibraryRoutes,
  teacherLibraryRoutes,
} from './routes/TeacherRoutes';
import { sharedMemberRoutes, studentRoutes } from './routes/StudentRoutes';
import { clearAllWorksheetAttemptRecovery } from './features/worksheet/student/worksheetRecovery';
import {
  installDeployUpdateRecovery,
  startDeployVersionWatch,
} from './app/deployUpdateRecovery';
import { Forbidden, LoginLocal, Notifications, Profile, Settings, UserDetail } from './routes/lazyPages';
import Home from './pages/Home';
import { AppRootLayout } from './routes/AppRootLayout';
import './styles.css';
import './styles/globals.css';
import './plugins/i18n';
import './utils/debugHelpers';

installDeployUpdateRecovery();
const runningBuildVersion = import.meta.env.VITE_APP_BUILD_VERSION as string | undefined;
if (runningBuildVersion) {
  startDeployVersionWatch(runningBuildVersion);
}

// Initialize Sentry ONLY in production environment (master branch)
// This prevents sending metrics from development/staging environments
if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: 'https://0e1da58776ed848d88570434acf95a8d@o4510476894601216.ingest.us.sentry.io/4510476894601216',

    // Enable sending default PII data (IP address, etc.)
    sendDefaultPii: true,

    // Integrations for comprehensive tracking
    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration({
        enableInp: true, // Track Interaction to Next Paint
      }),
      // Session Replay for debugging user sessions
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin],
        networkCaptureBodies: true,
      }),
      // Capture console logs as breadcrumbs
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      // HTTP client integration for fetch/XHR error tracking
      Sentry.httpClientIntegration(),
      // Capture console.error and console.warn to Sentry
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),
    ],

    // Tracing - Capture 100% of the transactions
    tracesSampleRate: 1.0,

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/gioitienganh\.vn\/api/,
      /^https:\/\/api\.gioitienganh\.vn/,
    ],

    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions for replay (change to 1.0 in development)
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors for replay

    // Enable logs to be sent to Sentry
    _experiments: {
      enableLogs: true,
    },

    // Environment tracking
    environment: import.meta.env.MODE,

    // Release tracking
    release: `gioitienganh-frontend@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

    // Disable debug logs
    debug: false,

    // Attach stack traces to all events
    attachStacktrace: true,

    // Enable automatic session tracking
    autoSessionTracking: true,

    // Before send hook for filtering/modifying events
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      // Filter out third-party errors that we can't control
      const errorMessage = event.exception?.values?.[0]?.value || '';
      const errorType = event.exception?.values?.[0]?.type || '';

      // Ignore errors from Zalo in-app browser SDK
      if (
        errorMessage.includes('zaloJSV2') ||
        errorMessage.includes('ZaloJavaScriptInterface') ||
        (errorType === 'ReferenceError' && errorMessage.includes('zalo'))
      ) {
        return null; // Don't send this error to Sentry
      }

      // Ignore common third-party script errors
      const ignoredPatterns = [
        /fbq/i, // Facebook Pixel
        /gtag/i, // Google Analytics
        /clarity/i, // Microsoft Clarity
        /hotjar/i, // Hotjar
        /intercom/i, // Intercom
        /ResizeObserver loop/i, // Browser resize observer warning
        /Loading chunk .* failed/i, // Dynamic import failures (handled separately)
      ];

      if (ignoredPatterns.some((pattern) => pattern.test(errorMessage))) {
        return null;
      }

      // Add user context if authenticated
      if (keycloak && keycloak.authenticated) {
        const userInfo = getUserInfo();
        if (userInfo) {
          event.user = {
            id: userInfo.id,
            email: userInfo.email,
            username: userInfo.username,
          };
          // Add custom tags for user roles
          event.tags = {
            ...event.tags,
            userRoles: userInfo.roles?.join(',') || 'none',
          };
        }
      }
      return event;
    },

    // Ignore specific errors by message pattern
    ignoreErrors: [
      // Zalo in-app browser
      /zaloJSV2/i,
      /ZaloJavaScriptInterface/i,
      // Dynamic import failures (handled by lazyWithRetry with auto-reload)
      /Failed to fetch dynamically imported module/i,
      /Loading chunk .* failed/i,
      /Loading CSS chunk .* failed/i,
      /Failed to load module script/i,
      // Network errors
      /Network request failed/i,
      /Load failed/i,
      // Browser extensions
      /chrome-extension/i,
      /moz-extension/i,
      // Resize observer (non-critical)
      /ResizeObserver loop/i,
      // Audio context race conditions (non-critical, handled gracefully)
      /Cannot read properties of null \(reading 'sampleRate'\)/i,
    ],
  });
} else {
  // In non-production environments, log that Sentry is disabled
  console.info('🔒 Sentry is disabled in non-production environment');
}

// Disable console.* trong production ngay từ đầu
disableConsoleInProduction();

// Initialize version service and make it globally accessible
versionService.initVersion().catch((err) => {
  logger.error('Failed to initialize version service:', err);
});
(window as any).__versionService = versionService;

const router = createBrowserRouter([
  { path: '/login', element: <RedirectIfAuthed /> },
  {
    path: '/login-local',
    element: (
      <React.Suspense fallback={<LoadingFallback />}>
        <LoginLocal />
      </React.Suspense>
    ),
  },
  {
    path: '/forbidden',
    element: (
      <React.Suspense fallback={<LoadingFallback />}>
        <Forbidden />
      </React.Suspense>
    ),
  },
  { path: '/auth/callback', element: <LoadingFallback /> },
  {
    path: '/',
    element: (
      <React.Suspense fallback={<LoadingFallback />}>
        <AppRootLayout />
      </React.Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <React.Suspense fallback={null}>
            <Home />
          </React.Suspense>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <React.Suspense fallback={null}>
            <UserDetail />
          </React.Suspense>
        ),
      },
      ...teacherDashboardRoutes,
      ...adminUserRoutes,
      {
        path: 'profile',
        element: (
          <React.Suspense fallback={null}>
            <Profile />
          </React.Suspense>
        ),
      },
      ...teacherClassroomRoutes,
      ...adminStudentClassroomRoutes,
      {
        path: 'classrooms/:code',
        element: (
          <RequireAuth>
            <ClassroomRouteGuard />
          </RequireAuth>
        ),
      },
      ...teacherAssignmentRoutes,
      ...adminStudentRoutes,
      ...teacherContentRoutes,
      ...adminSystemRoutes,
      ...sharedMemberRoutes,
      ...teacherLibraryRoutes,
      ...studentRoutes,
      ...teacherExerciseLibraryRoutes,
      {
        path: 'notifications',
        element: (
          <React.Suspense fallback={null}>
            <Notifications />
          </React.Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <React.Suspense fallback={null}>
            <Settings />
          </React.Suspense>
        ),
      },
    ],
  },
]);

// Initialize Keycloak with proper session management
async function initApp() {
  const root = createRoot(document.getElementById('root')!);
  if (typeof document !== 'undefined') {
    portalContainerRef.current = document.getElementById('portal-root');
  }

  // Show loading initially
  root.render(<LoadingFallback />);

  try {
    await initMediaHosts();
    const authenticated = await initKeycloakWithFallback();

    logger.log('🔐 Keycloak init complete. Authenticated:', authenticated);

    const pathname = window.location.pathname;
    const isPublicWhenLoggedOut =
      pathname === '/' || pathname === '/login' || pathname === '/login-local';

    // If not authenticated and not on login page, redirect to login
    if (!authenticated && !isPublicWhenLoggedOut) {
      logger.log('❌ Not authenticated, redirecting to login...');

      // Save intended URL and hash separately to sessionStorage before redirecting
      const intendedUrl = window.location.pathname + window.location.search;
      const intendedHash = window.location.hash;
      try {
        sessionStorage.setItem('intendedUrl', intendedUrl);
        if (intendedHash) {
          sessionStorage.setItem('intendedHash', intendedHash);
          logger.log('💾 Saved intended hash:', intendedHash);
        }
        logger.log('💾 Saved intended URL:', intendedUrl);
      } catch (e) {
        logger.error('Failed to save intended URL:', e);
      }

      window.location.href = '/login';
      return; // Don't render app yet
    }

    // If authenticated, check for intended URL and hash, then redirect
    if (authenticated) {
      try {
        const intendedUrl = sessionStorage.getItem('intendedUrl');
        const intendedHash = sessionStorage.getItem('intendedHash');

        if (intendedUrl && intendedUrl !== '/login' && intendedUrl !== '/login-local') {
          logger.log('✅ Found intended URL, redirecting to:', intendedUrl);
          sessionStorage.removeItem('intendedUrl');

          // Restore hash if exists
          let finalUrl =
            intendedUrl === '/landing.html' || intendedUrl === '/giao-vien.html'
              ? '/'
              : intendedUrl;
          if (intendedHash) {
            finalUrl = finalUrl + intendedHash;
            sessionStorage.removeItem('intendedHash');
            logger.log('✅ Restoring hash:', intendedHash);
          }

          window.location.href = finalUrl;
          return; // Don't render app yet, let redirect happen
        } else if (intendedHash && window.location.pathname === '/') {
          // User is on home page after auth, restore hash only
          logger.log('✅ Restoring hash on home page:', intendedHash);
          sessionStorage.removeItem('intendedHash');
          window.location.hash = intendedHash;
        }
      } catch (e) {
        logger.error('Failed to retrieve intended URL:', e);
      }
    }

    // Token refresh is handled by Keycloak event handlers below
    // No need for manual setInterval - keycloak-js handles this automatically

    // Set up Keycloak event handlers
    keycloak.onTokenExpired = () => {
      logger.log('🔄 Token expired, attempting refresh...');
      keycloak.updateToken(5).catch(() => {
        logger.log('❌ Token refresh failed, redirecting to login');
        window.location.href = '/login';
      });
    };

    keycloak.onAuthLogout = () => {
      // Never leave a student's worksheet recovery copy behind after logout.
      clearAllWorksheetAttemptRecovery();
      logger.log('👋 User logged out, redirecting to login');
      window.location.href = '/login';
    };

    keycloak.onAuthRefreshSuccess = () => {
      logger.log('✅ Token refreshed successfully');
    };

    keycloak.onAuthRefreshError = () => {
      logger.log('❌ Token refresh failed, redirecting to login');
      window.location.href = '/login';
    };
  } catch (error) {
    logger.error('Failed to initialize Keycloak:', error);
    // On error, redirect to login
    window.location.href = '/login';
    return;
  }

  // Hide the inline loading spinner by adding class to html element
  document.documentElement.classList.add('app-loaded');

  // Render the app only after auth check is complete
  root.render(
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>,
  );

  // Auto-enable debug in development to catch overflow issues
  if (import.meta.env.DEV) {
    // Wait for app to render
    setTimeout(() => {
      logger.log('🛠️ Development mode: Debug helpers available');
      logger.log('💡 Run enableDebugMode() to find overflow issues');
      logger.log('⚠️ If you see horizontal scrollbar, there are overflow issues!');
    }, 1000);
  }
}

initApp();
